import { useEffect, useState } from 'react';
import { Home, Loader2, Save, RotateCcw, ArrowUp, ArrowDown, Eye, EyeOff, ImagePlus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { DEFAULT_SITE_CONFIG, mergeConfig } from '../../lib/siteConfig';

const ICON_OPTIONS = [
  'Users', 'GraduationCap', 'BarChart3', 'ShieldCheck', 'Megaphone', 'Handshake', 'Map', 'Newspaper',
  'MapPin', 'Bot', 'Globe', 'Building2', 'FileText', 'LineChart', 'TrendingUp', 'Sparkles', 'Landmark',
];

const TABS = ['Menú', 'Secciones', 'Hero', 'Estadísticas', 'Beneficios', 'Connect', 'Shorts', 'Módulos', 'CTA', 'Footer', 'Redes'];

const inputCls =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';
const labelCls = 'mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500';

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={3} className={inputCls} data-testid={testid} />
      ) : (
        <input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success('Imagen subida.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-2">
        <input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="https://…" data-testid={testid} />
        <label className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-cnib-blue px-3 py-2 text-xs font-bold text-white ${uploading ? 'opacity-60' : ''}`}>
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          Subir
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      </div>
      {value && <img src={value} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
    </div>
  );
}

function IconSelect({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      {ICON_OPTIONS.map((i) => (
        <option key={i} value={i}>{i}</option>
      ))}
    </select>
  );
}

function SortableRows({ items, onChange, renderRow, onRemove }) {
  const move = (i, dir) => {
    const arr = [...items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex flex-col">
            <button type="button" onClick={() => move(i, -1)} className="text-slate-400 hover:text-cnib-blue" aria-label="Subir"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-slate-400 hover:text-cnib-blue" aria-label="Bajar"><ArrowDown className="h-3.5 w-3.5" /></button>
          </div>
          <button
            type="button"
            onClick={() => onChange(items.map((it, idx) => (idx === i ? { ...it, visible: !it.visible } : it)))}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${item.visible ? 'bg-emerald-500/10 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}
            title={item.visible ? 'Visible' : 'Oculto'}
          >
            {item.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
          <div className="flex-1">{renderRow(item, i)}</div>
          {onRemove && (
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-500" aria-label="Eliminar">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminHomeConfig() {
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState('Menú');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/site-config')
      .then(({ data }) => setConfig(Object.keys(data || {}).length ? mergeConfig(DEFAULT_SITE_CONFIG, data) : DEFAULT_SITE_CONFIG))
      .catch(() => setConfig(DEFAULT_SITE_CONFIG))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !config) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
      </div>
    );
  }

  const set = (path, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/site-config', config);
      toast.success('Portada actualizada. Recarga la home para verla.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (window.confirm('¿Restaurar todos los valores por defecto? Guarda para aplicar.')) {
      setConfig(JSON.parse(JSON.stringify(DEFAULT_SITE_CONFIG)));
      toast.info('Valores por defecto cargados. Presiona Guardar para aplicar.');
    }
  };

  const seedDefaults = async () => {
    if (!window.confirm('¿Cargar contenido de ejemplo (noticias, eventos, Nosotros, shorts y secciones)? Solo rellena lo que esté vacío, no sobrescribe tu contenido actual.')) return;
    try {
      const { data } = await api.post('/admin/seed-defaults');
      toast.success(`Contenido base cargado. ${JSON.stringify(data.created)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el seed');
    }
  };

  const updateItem = (path, i, patchObj) => {
    const arr = [...eval(`config.${path}`)];
    arr[i] = { ...arr[i], ...patchObj };
    set(path, arr);
  };

  return (
    <div className="space-y-6" data-testid="admin-home-config-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">CMS</p>
          <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
            <Home className="h-7 w-7 text-cnib-blue" /> Configuración de la Home
          </h1>
          <p className="mt-1 text-sm text-slate-500">Edita textos, imágenes, menú y secciones del portal público.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={seedDefaults} className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-50 px-5 py-2.5 text-sm font-bold text-emerald-700 transition-colors hover:bg-emerald-100" data-testid="home-config-seed-defaults" title="Idempotente — solo rellena lo que falta">
            <RotateCcw className="h-4 w-4" /> Cargar contenido base
          </button>
          <button onClick={reset} className="flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100" data-testid="home-config-reset">
            <RotateCcw className="h-4 w-4" /> Restaurar
          </button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60" data-testid="home-config-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar cambios
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="home-config-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`home-config-tab-${t.toLowerCase()}`}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === t ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
        {tab === 'Menú' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Ordena con las flechas, oculta con el ojo. Los cambios se ven en el menú público.</p>
            <SortableRows
              items={config.menu}
              onChange={(arr) => set('menu', arr)}
              onRemove
              renderRow={(item, i) => (
                <div className="grid gap-2 sm:grid-cols-2">
                  <input value={item.label} onChange={(e) => updateItem('menu', i, { label: e.target.value })} className={inputCls} placeholder="Etiqueta" data-testid={`menu-label-${i}`} />
                  <input value={item.to} onChange={(e) => updateItem('menu', i, { to: e.target.value })} className={inputCls} placeholder="/ruta" data-testid={`menu-to-${i}`} />
                </div>
              )}
            />
            <button
              type="button"
              onClick={() => set('menu', [...config.menu, { label: 'Nuevo enlace', to: '/', visible: true }])}
              className="flex items-center gap-2 rounded-full bg-cnib-blue px-4 py-2 text-xs font-bold text-white hover:bg-cnib-bluehover"
              data-testid="menu-add-button"
            >
              <Plus className="h-3.5 w-3.5" /> Agregar enlace
            </button>
          </div>
        )}

        {tab === 'Secciones' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Define qué bloques se muestran en la home y en qué orden.</p>
            <SortableRows
              items={config.sections}
              onChange={(arr) => set('sections', arr)}
              renderRow={(item) => <p className="py-1 text-sm font-bold text-slate-800">{item.label}</p>}
            />
          </div>
        )}

        {tab === 'Hero' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Field label="Volanta (texto pequeño)" value={config.hero.eyebrow} onChange={(v) => set('hero.eyebrow', v)} testid="hero-eyebrow-input" />
            <Field label="Subtítulo" value={config.hero.subtitle} onChange={(v) => set('hero.subtitle', v)} testid="hero-subtitle-input" />
            {config.hero.lines.map((line, i) => (
              <Field key={i} label={`Línea del título ${i + 1}${i === 1 ? ' (naranja)' : ''}`} value={line} onChange={(v) => { const lines = [...config.hero.lines]; lines[i] = v; set('hero.lines', lines); }} testid={`hero-line-${i}`} />
            ))}
            <Field label="Botón principal" value={config.hero.cta_primary} onChange={(v) => set('hero.cta_primary', v)} testid="hero-cta-primary" />
            <Field label="Botón secundario" value={config.hero.cta_secondary} onChange={(v) => set('hero.cta_secondary', v)} testid="hero-cta-secondary" />
            <ImageField label="Imagen de fondo del hero" value={config.hero.bg_image} onChange={(v) => set('hero.bg_image', v)} testid="hero-bg-image" />
            <ImageField label="Mockup teléfono (mapa)" value={config.hero.phone_map} onChange={(v) => set('hero.phone_map', v)} testid="hero-phone-map" />
            <ImageField label="Mockup teléfono (propiedad)" value={config.hero.phone_property} onChange={(v) => set('hero.phone_property', v)} testid="hero-phone-property" />
            <div className="lg:col-span-2">
              <p className={labelCls}>Mini beneficios del hero (3)</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {config.hero.features.map((f, i) => (
                  <div key={i} className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
                    <IconSelect value={f.icon} onChange={(v) => updateItem('hero.features', i, { icon: v })} />
                    <input value={f.text} onChange={(e) => updateItem('hero.features', i, { text: e.target.value })} className={inputCls} />
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <Field label="Cinta de cámaras (una por línea)" value={config.marquee.join('\n')} onChange={(v) => set('marquee', v.split('\n').map((s) => s.trim()).filter(Boolean))} textarea testid="marquee-items-input" />
            </div>
          </div>
        )}

        {tab === 'Estadísticas' && (
          <div className="grid gap-3 sm:grid-cols-2">
            {config.stats.map((s, i) => (
              <div key={i} className="grid grid-cols-[70px_90px_1fr] items-end gap-2 rounded-xl border border-slate-200 bg-white p-3">
                <div>
                  <label className={labelCls}>Prefijo</label>
                  <input value={s.prefix} onChange={(e) => updateItem('stats', i, { prefix: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Valor</label>
                  <input type="number" value={s.value} onChange={(e) => updateItem('stats', i, { value: Number(e.target.value) })} className={inputCls} data-testid={`stat-value-${i}`} />
                </div>
                <div>
                  <label className={labelCls}>Etiqueta</label>
                  <input value={s.label} onChange={(e) => updateItem('stats', i, { label: e.target.value })} className={inputCls} data-testid={`stat-label-${i}`} />
                </div>
                <div className="col-span-3">
                  <IconSelect value={s.icon} onChange={(v) => updateItem('stats', i, { icon: v })} />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'Beneficios' && (
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <Field label="Volanta" value={config.benefits.eyebrow} onChange={(v) => set('benefits.eyebrow', v)} />
              <Field label="Título" value={config.benefits.title} onChange={(v) => set('benefits.title', v)} />
              <Field label="Descripción" value={config.benefits.description} onChange={(v) => set('benefits.description', v)} textarea />
            </div>
            <p className={labelCls}>Tarjetas de beneficio</p>
            <div className="grid gap-3 lg:grid-cols-2">
              {config.benefits.items.map((b, i) => (
                <div key={i} className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
                  <div className="grid grid-cols-[140px_1fr] gap-2">
                    <IconSelect value={b.icon} onChange={(v) => updateItem('benefits.items', i, { icon: v })} />
                    <input value={b.title} onChange={(e) => updateItem('benefits.items', i, { title: e.target.value })} className={inputCls} placeholder="Título" />
                  </div>
                  <textarea value={b.text} onChange={(e) => updateItem('benefits.items', i, { text: e.target.value })} rows={2} className={inputCls} placeholder="Texto" />
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'Connect' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Field label="Volanta" value={config.connect.eyebrow} onChange={(v) => set('connect.eyebrow', v)} />
            <Field label="Título" value={config.connect.title} onChange={(v) => set('connect.title', v)} />
            <Field label="Subtítulo" value={config.connect.subtitle} onChange={(v) => set('connect.subtitle', v)} />
            <Field label="Texto junto al QR" value={config.connect.qr_text} onChange={(v) => set('connect.qr_text', v)} />
            <Field label="Título de la tarjeta" value={config.connect.card_title} onChange={(v) => set('connect.card_title', v)} />
            <Field label="Texto de la tarjeta" value={config.connect.card_text} onChange={(v) => set('connect.card_text', v)} textarea />
            <Field label="Botón de la tarjeta" value={config.connect.card_cta} onChange={(v) => set('connect.card_cta', v)} />
            <div>
              <p className={labelCls}>Checklist (una por línea)</p>
              <textarea value={config.connect.checklist.join('\n')} onChange={(e) => set('connect.checklist', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} rows={5} className={inputCls} data-testid="connect-checklist-input" />
            </div>
          </div>
        )}

        {tab === 'Módulos' && (
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <Field label="Volanta" value={config.modules.eyebrow} onChange={(v) => set('modules.eyebrow', v)} />
              <Field label="Título" value={config.modules.title} onChange={(v) => set('modules.title', v)} />
              <Field label="Descripción" value={config.modules.description} onChange={(v) => set('modules.description', v)} textarea />
            </div>
            <SortableRows
              items={config.modules.items}
              onChange={(arr) => set('modules.items', arr)}
              renderRow={(item, i) => (
                <div className="grid gap-2 sm:grid-cols-[130px_1fr_1fr]">
                  <IconSelect value={item.icon} onChange={(v) => updateItem('modules.items', i, { icon: v })} />
                  <input value={item.title} onChange={(e) => updateItem('modules.items', i, { title: e.target.value })} className={inputCls} placeholder="Título" />
                  <input value={item.description} onChange={(e) => updateItem('modules.items', i, { description: e.target.value })} className={inputCls} placeholder="Descripción" />
                </div>
              )}
            />
          </div>
        )}

        {tab === 'CTA' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Field label="Título" value={config.cta.title} onChange={(v) => set('cta.title', v)} textarea />
            <Field label="Texto del panel" value={config.cta.text} onChange={(v) => set('cta.text', v)} textarea />
            <Field label="Texto del botón" value={config.cta.button} onChange={(v) => set('cta.button', v)} />
            <ImageField label="Imagen del banner" value={config.cta.image} onChange={(v) => set('cta.image', v)} testid="cta-image-input" />
            <div className="lg:col-span-2">
              <p className={labelCls}>Pilares (4)</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {config.cta.pillars.map((p, i) => (
                  <div key={i} className="grid grid-cols-[140px_1fr] gap-2 rounded-xl border border-slate-200 bg-white p-3">
                    <IconSelect value={p.icon} onChange={(v) => updateItem('cta.pillars', i, { icon: v })} />
                    <input value={p.label} onChange={(e) => updateItem('cta.pillars', i, { label: e.target.value })} className={inputCls} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'Footer' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Field label="Texto institucional" value={config.footer.about} onChange={(v) => set('footer.about', v)} textarea testid="footer-about-input" />
            <Field label="Dirección" value={config.footer.address} onChange={(v) => set('footer.address', v)} />
            <Field label="Teléfono" value={config.footer.phone} onChange={(v) => set('footer.phone', v)} />
            <Field label="Correo" value={config.footer.email} onChange={(v) => set('footer.email', v)} />
            <Field label="Sitio web" value={config.footer.web} onChange={(v) => set('footer.web', v)} />
          </div>
        )}

        {tab === 'Redes' && (
          <div className="grid gap-4 lg:grid-cols-2" data-testid="admin-home-social">
            <p className="lg:col-span-2 text-xs text-slate-500">
              Estas URLs aparecen en el pie de página de todas las páginas públicas y en los enlaces compartidos.
              Dejá en blanco las redes que todavía no estén activas.
            </p>
            <Field label="Instagram" value={config.social?.instagram} onChange={(v) => set('social.instagram', v)} testid="social-instagram-input" />
            <Field label="Facebook" value={config.social?.facebook} onChange={(v) => set('social.facebook', v)} testid="social-facebook-input" />
            <Field label="LinkedIn" value={config.social?.linkedin} onChange={(v) => set('social.linkedin', v)} testid="social-linkedin-input" />
            <Field label="TikTok" value={config.social?.tiktok} onChange={(v) => set('social.tiktok', v)} testid="social-tiktok-input" />
            <Field label="YouTube" value={config.social?.youtube} onChange={(v) => set('social.youtube', v)} testid="social-youtube-input" />
            <Field label="X / Twitter" value={config.social?.twitter} onChange={(v) => set('social.twitter', v)} testid="social-twitter-input" />
          </div>
        )}

        {tab === 'Shorts' && (
          <div className="space-y-5" data-testid="admin-home-shorts">
            <div className="grid gap-4 lg:grid-cols-2">
              <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
                <input
                  type="checkbox"
                  checked={config.shorts?.enabled !== false}
                  onChange={(e) => set('shorts.enabled', e.target.checked)}
                  data-testid="shorts-enabled-toggle"
                />
                <span className="font-semibold">Mostrar carrusel de shorts en el home</span>
              </label>
              <Field label="Encabezado chico (eyebrow)" value={config.shorts?.eyebrow} onChange={(v) => set('shorts.eyebrow', v)} testid="shorts-eyebrow-input" />
              <Field label="Título" value={config.shorts?.title} onChange={(v) => set('shorts.title', v)} testid="shorts-title-input" />
              <Field label="Subtítulo" value={config.shorts?.subtitle} onChange={(v) => set('shorts.subtitle', v)} textarea testid="shorts-subtitle-input" />
            </div>
            <p className="text-xs text-slate-500">
              Para cambiar la posición del carrusel en el home, abrí la pestaña <b>Secciones</b> y reordená
              "Shorts / Reels / TikToks" con las flechas.
            </p>
            <ShortsItemsEditor
              items={config.shorts?.items || []}
              onChange={(arr) => set('shorts.items', arr)}
              onUpdate={(i, p) => updateItem('shorts.items', i, p)}
              inputCls={inputCls}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ShortsItemsEditor({ items, onChange, onUpdate, inputCls }) {
  const add = () => onChange([...items, { thumbnail: '', link: '', title: '' }]);
  const move = (i, dir) => {
    const arr = [...items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const del = (i) => {
    if (!window.confirm('¿Eliminar este short?')) return;
    onChange(items.filter((_, idx) => idx !== i));
  };
  return (
    <div className="space-y-3" data-testid="shorts-items-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-700">Shorts cargados ({items.length})</h3>
        <button onClick={add} type="button" className="inline-flex items-center gap-1.5 rounded-full bg-cnib-navy px-4 py-2 text-xs font-bold text-white hover:bg-cnib-blue" data-testid="shorts-add">
          <Plus className="h-3.5 w-3.5" /> Agregar short
        </button>
      </div>
      {items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 bg-white py-10 text-center text-xs text-slate-500">
          Sin shorts cargados aún. Agregá el primero con miniatura y link.
        </p>
      ) : (
        items.map((item, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`shorts-item-${i}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">#{i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded-full bg-slate-100 p-1.5 text-slate-500 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="rounded-full bg-slate-100 p-1.5 text-slate-500 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => del(i)} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="grid gap-3 lg:grid-cols-[200px_1fr]">
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">Miniatura</p>
                {item.thumbnail ? (
                  <img src={item.thumbnail} alt="" className="mb-2 aspect-[9/16] w-full rounded-xl object-cover shadow" />
                ) : (
                  <div className="mb-2 flex aspect-[9/16] w-full items-center justify-center rounded-xl bg-slate-100 text-xs text-slate-400">Sin imagen</div>
                )}
                <ImageField label="" value={item.thumbnail} onChange={(v) => onUpdate(i, { thumbnail: v })} testid={`shorts-thumb-${i}`} />
              </div>
              <div className="grid content-start gap-2">
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">Título</p>
                  <input value={item.title || ''} onChange={(e) => onUpdate(i, { title: e.target.value })} className={inputCls} placeholder="Ej. Entrevista con el presidente" data-testid={`shorts-title-${i}`} />
                </div>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">Link del Reel / Short / TikTok</p>
                  <input value={item.link || ''} onChange={(e) => onUpdate(i, { link: e.target.value })} className={inputCls} placeholder="https://www.tiktok.com/@cnibolivia/video/..." data-testid={`shorts-link-${i}`} />
                </div>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">Plataforma</p>
                  <select
                    value={item.platform || 'auto'}
                    onChange={(e) => onUpdate(i, { platform: e.target.value === 'auto' ? undefined : e.target.value })}
                    className={inputCls}
                    data-testid={`shorts-platform-${i}`}
                  >
                    <option value="auto">Detectar automáticamente</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="tiktok">TikTok</option>
                    <option value="youtube">YouTube</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
